"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  type PrivateKeyAccount,
  type TypedDataParameter,
  type WalletClient,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { useAccount } from "#/store/wallet/account/account";
import { type CancelAllOrdersRequest } from "#/types/api";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { EIP712Domain } from "#/utils/signature";

import { cancelAllOrders } from "../kyan.service";
import {
  type CancelOrdersResponse,
  type WithoutSignature,
} from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";
import { useTradingSession } from "./useTradingSession";

const CancelAllOrdersType: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "maker", type: "address" },
];

const signCancelAllOrders = async (
  cancelRequest: Omit<
    CancelAllOrdersRequest,
    "signature" | "signature_deadline"
  >,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<CancelAllOrdersRequest> => {
  const signature_deadline = getSignatureDeadline();

  return {
    ...cancelRequest,
    signature_deadline,
    signature: await walletClient.signTypedData({
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { CancelAllOrdersType },
      primaryType: "CancelAllOrdersType",
      message: {
        deadline: signature_deadline,
        maker: cancelRequest.maker,
        market: cancelRequest.market,
      },
    }),
  };
};

export const useCancelAllOrders = (
  options?: UseMutationOptions<
    CancelOrdersResponse,
    Error,
    Pick<CancelAllOrdersRequest, "market">
  >,
) => {
  const { account } = useAccount();
  const { walletClient } = useWalletClient();
  const { shouldUseSessionHash } = useTradingSession();

  return useMutation({
    ...options,
    mutationFn: async ({ market }) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const data: WithoutSignature<CancelAllOrdersRequest> = {
        maker: account!,
        market,
      };

      if (shouldUseSessionHash) {
        return defaultApiResponseHandler({
          serviceName: "Cancel All Orders",
          payload: {
            data,
            includeSessionHash: true,
          },
          service: cancelAllOrders,
        });
      }

      const signedCancelAllOrders = await signCancelAllOrders(
        data,
        walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
      );

      return defaultApiResponseHandler({
        serviceName: "Cancel All Orders",
        payload: { data: signedCancelAllOrders },
        service: cancelAllOrders,
      });
    },
  });
};
